import Image from "next/image";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import type { Community } from "@/types/database";

// Legacy community format (from mock data)
interface LegacyCommunity {
  id: string;
  name: string;
  location: string;
  description: string;
  image: string;
  amenities: string[];
}

// Support both legacy and database formats
type CommunityInput = LegacyCommunity | Community;

interface CommunityCardProps {
  community: CommunityInput;
  className?: string;
  index?: number;
}

// Type guard to check if community is database format
function isDatabaseCommunity(community: CommunityInput): community is Community {
  return 'image_url' in community || 'highlights' in community;
}

export function CommunityCard({ community, className, index = 0 }: CommunityCardProps) {
  // Normalize community data
  const id = String(community.id);
  const name = community.name;
  const location = isDatabaseCommunity(community) ? community.city : community.location;
  const description = community.description || "";
  const image = isDatabaseCommunity(community)
    ? (community.image_url || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop")
    : community.image;

  // Debug log for image - checking source
  console.log(`Community Image [${name}]:`, image);

  const finalImage = image || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop";

  return (
    <div
      className={cn(
        "group grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-16 items-center",
        className
      )}
    >
      {/* Image Container - Left Side (Wider - Spans 3 cols) */}
      <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden rounded-xl w-full col-span-1 md:col-span-3 shadow-xl group-hover:shadow-2xl transition-all duration-300">
        <Image
          src={finalImage}
          alt={name}
          fill
          sizes="(max-width: 768px) 100vw, 60vw"
          className="object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
        />
      </div>

      {/* Content - Right Side (Spans 2 cols) */}
      <div className="flex flex-col justify-center text-left col-span-1 md:col-span-2">

        <h3 className="font-serif text-3xl md:text-5xl text-primary tracking-wider mb-3">
          {name}
        </h3>

        <div className="flex items-center gap-2 text-muted-foreground mb-8">
          <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[8px]">📍</span>
          <span className="text-sm uppercase tracking-wider">{location}</span>
        </div>

        <div className="font-sans text-base text-muted-foreground leading-relaxed mb-8 max-w-sm">
          <p className="mb-4">{description}</p>
          <p className="hidden md:block text-muted-foreground/80">
            Experience the epitome of luxury living where modern design meets timeless elegance.
            {name} offers a curated lifestyle with exclusive access to premium amenities.
          </p>
        </div>

        <div className="mb-10">
          <span className="text-sm text-primary/70 font-medium block mb-1">
            1, 2 & 3 Bedroom Apartments
          </span>
        </div>

        <div>
          <Button
            variant="outline"
            className="rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300"
            asChild
          >
            <Link href={`/communities/${id}`}>
              Discover More
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
